import { formatDate } from 'date-fns'
import { ReadonlyURLSearchParams, useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import { BaseFilters } from './model'
import { OidcSession } from './oidc-config'
import { useOidcSession } from './useOidcSession'

export default function useFetchPagedFilteredAPI<Type, Filters extends BaseFilters>(
    key: (authSession: OidcSession | null, authenticating: boolean, searchParams: ReadonlyURLSearchParams) => string | null,
    fetch: (authSession: OidcSession | null, authenticating: boolean, key: string) => Promise<{total: number, data: Type[]}>,
) {
    const router = useRouter()
    const searchParams = useSearchParams()

    const { data: authSession, status: authenticating } = useOidcSession()

    const { data: page, error, isLoading } = useSWR(
        () => key(authSession, authenticating === 'loading', searchParams!) ?? null,
        (key: string) => fetch(authSession, authenticating === 'loading', key),
        {
            shouldRetryOnError: true,
            revalidateOnMount: true,
            revalidateOnFocus: false,
            revalidateFirstPage: false,
        },
    )

    const { data, total: count } = page ? { data: page.data, total: page.total } : { data: undefined, total: undefined }

    // Mettre à jour l'URL en fonction des nouvelles valeurs de filtres
    const updateFilters = (filters: Partial<Filters>, replace?: boolean) => {
        const params = getParams(filters)

        const url = {
            pathname: window.location.pathname,
            hash: window.location.hash,
            query: params.toString() || undefined
        }

        if (replace) {
            router.replace(url, undefined, { shallow: true })
        } else {
            router.push(url, undefined, { shallow: true })
        }
    }

    // Réinitialiser les filtres en naviguant vers une URL "nettoyée"
    // Seules les options de tri sont conservées
    const resetFilters = () => {
        const params = new URLSearchParams()

        if (searchParams?.get("tri")) {
            params.append("tri", searchParams.get("tri")!)
        }

        if (searchParams?.get("ordre")) {
            params.append("ordre", searchParams.get("ordre")!)
        }

        router.push({ pathname: window.location.pathname, query: params.toString() }, undefined, { shallow: true })
    }

    // Des filtres sont-ils actifs ?
    // Les filtres de page et de tri sont exclus
    const hasActiveFilters = () => {
        const params = new URLSearchParams(searchParams!)
        params.delete("entreprise") // Ignore si passé par myAtlas
        params.delete("page")
        params.delete("tri")
        params.delete("ordre")

        return !!params.toString()
    }

    // Construire un objet URLSearchParams en fonction des valeurs de filtres
    const getParams = (filters: Partial<Filters>): URLSearchParams => {
        const params = new URLSearchParams(searchParams!)
        // params.delete("entreprise") // Conserve paramètre passé par myAtlas
        params.delete("page")

        for (const [key, value] of Object.entries(filters)) {
            params.delete(key)

            if (typeof value === "string" && value.length > 0) { // ex: recherche=anglais
                params.set(key, value)
            } else if (typeof value === "number") { // ex: page=3
                params.set(key, value.toString())
            } else if (value === true) { // ex: libre-acces=true
                params.append(key, "true")
            } else if (Array.isArray(value)) { // ex: modalites=1&modalites=2
                value.sort().forEach(_value => params.append(key, _value))
            } else if (value instanceof Date) { // ex: date-debut=2026-01-01
                params.set(key, formatDate(value, "yyyy-MM-dd"))
            }
        }

        return params
    }

    return {
        data,
        count,
        error,
        loading: isLoading || data === undefined,
        updateFilters,
        resetFilters,
        hasActiveFilters,
    }
}
